// Browser side of the logo page: opening the picked file, looking at its
// pixels and drawing the final PNG. Rules/math live in logo.utils.ts.

import {
  drawnSize,
  FRAME,
  isSvgFile,
  MAX_SAVED_BYTES,
  OUTPUT_SIZES,
  Placement,
  Rect,
  sampleSize,
  summarizePixels,
  svgRenderSize,
  trimRect,
} from "./logo.utils";

export type LoadedLogo = {
  /** Object URL of the picture as the browser draws it (SVG made sized). */
  url: string;
  image: HTMLImageElement;
  width: number;
  height: number;
  isSvg: boolean;
  fileName: string;
  /** Box around the logo itself (null = nothing to trim). */
  trim: Rect | null;
  mostlyWhite: boolean;
};

const loadImage = (url: string) =>
  new Promise<HTMLImageElement>((resolve, reject) => {
    const image = new Image();
    image.onload = () => resolve(image);
    image.onerror = () => reject(new Error("image"));
    image.src = url;
  });

/** SVGs get an explicit size so they are drawn sharp (and at all). */
const sizedSvgBlob = async (file: Blob) => {
  const text = await file.text();
  const doc = new DOMParser().parseFromString(text, "image/svg+xml");
  const svg = doc.documentElement;
  if (!svg || svg.nodeName.toLowerCase() !== "svg") throw new Error("svg");
  const num = (value: string | null) =>
    value && !value.trim().endsWith("%") ? parseFloat(value) : null;
  const size = svgRenderSize(
    num(svg.getAttribute("width")),
    num(svg.getAttribute("height")),
    svg.getAttribute("viewBox"),
  );
  if (!svg.getAttribute("viewBox")) {
    const w = num(svg.getAttribute("width"));
    const h = num(svg.getAttribute("height"));
    if (w && h) svg.setAttribute("viewBox", `0 0 ${w} ${h}`);
  }
  svg.setAttribute("width", String(size.width));
  svg.setAttribute("height", String(size.height));
  svg.setAttribute("preserveAspectRatio", "xMidYMid meet");
  return new Blob([new XMLSerializer().serializeToString(svg)], {
    type: "image/svg+xml",
  });
};

const UNREADABLE =
  "This picture couldn't be opened. It may be damaged or in a format the browser can't read. Please save it again as PNG or JPG.";

export const loadLogoFile = async (file: File): Promise<LoadedLogo> => {
  const isSvg = isSvgFile(file);
  let blob: Blob = file;
  if (isSvg) {
    try {
      blob = await sizedSvgBlob(file);
    } catch {
      throw new Error(UNREADABLE);
    }
  }
  const url = URL.createObjectURL(blob);
  let image: HTMLImageElement;
  try {
    image = await loadImage(url);
  } catch {
    URL.revokeObjectURL(url);
    throw new Error(UNREADABLE);
  }
  const width = image.naturalWidth;
  const height = image.naturalHeight;
  if (!width || !height) {
    URL.revokeObjectURL(url);
    throw new Error(UNREADABLE);
  }

  let trim: Rect | null = null;
  let mostlyWhite = false;
  try {
    const sample = sampleSize(width, height);
    const canvas = document.createElement("canvas");
    canvas.width = sample.width;
    canvas.height = sample.height;
    const ctx = canvas.getContext("2d", { willReadFrequently: true });
    if (ctx) {
      ctx.drawImage(image, 0, 0, sample.width, sample.height);
      const data = ctx.getImageData(0, 0, sample.width, sample.height).data;
      const summary = summarizePixels(data, sample.width, sample.height);
      trim = trimRect(
        summary.bounds,
        sample.width,
        sample.height,
        width,
        height,
      );
      mostlyWhite = summary.mostlyWhite;
    }
  } catch {
    // Pixels can't be read (rare): no trimming, no white check.
  }

  return {
    url,
    image,
    width,
    height,
    isSvg,
    fileName: file.name,
    trim,
    mostlyWhite,
  };
};

const toBlob = (canvas: HTMLCanvasElement) =>
  new Promise<Blob>((resolve, reject) =>
    canvas.toBlob(
      (blob) => (blob ? resolve(blob) : reject(new Error("blob"))),
      "image/png",
    ),
  );

/** Draws the framed logo as a transparent PNG (1200 x 400, smaller if the file
 * would be too big). */
export const renderLogoPng = async (
  logo: LoadedLogo,
  crop: Rect,
  placement: Placement,
): Promise<{ blob: Blob; width: number; height: number }> => {
  const { width: dw, height: dh } = drawnSize(crop, placement.zoom);
  let last: { blob: Blob; width: number; height: number } | null = null;
  for (const size of OUTPUT_SIZES) {
    const k = size.width / FRAME.width;
    const canvas = document.createElement("canvas");
    canvas.width = size.width;
    canvas.height = size.height;
    const ctx = canvas.getContext("2d");
    if (!ctx) throw new Error("canvas");
    ctx.imageSmoothingEnabled = true;
    ctx.imageSmoothingQuality = "high";
    ctx.drawImage(
      logo.image,
      crop.x,
      crop.y,
      crop.width,
      crop.height,
      placement.x * k,
      placement.y * k,
      dw * k,
      dh * k,
    );
    const blob = await toBlob(canvas);
    last = { blob, width: size.width, height: size.height };
    if (blob.size <= MAX_SAVED_BYTES) return last;
  }
  if (!last) throw new Error("canvas");
  return last;
};

/** Opens the saved logo for adjusting; null when the browser may not read it. */
export const fetchLogoAsFile = async (url: string): Promise<File | null> => {
  try {
    const response = await fetch(url, { mode: "cors" });
    if (!response.ok) return null;
    const blob = await response.blob();
    const type = blob.type.startsWith("image/") ? blob.type : "image/png";
    const ext = type === "image/svg+xml" ? "svg" : type.split("/")[1] || "png";
    return new File([blob], `current-logo.${ext}`, { type });
  } catch {
    return null;
  }
};
